// Make a login for someone, without a terminal.
//
// Accounts live in environment variables, so this cannot create one — it makes
// the line you paste into Vercel. That split is the point: who may log in stays
// a deployment decision with a trail behind it, while the tedious half (running
// a hashing script on a laptop and assembling the string by hand) goes away.
//
// The password is sent once, hashed on the server, and dropped. What comes back
// is `name:scrypt$…`, so a real password never lands in an environment variable.
import { useState } from 'react';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

/**
 * A password somebody can read down a phone but not guess.
 *
 * Three groups of four rather than one long string: easier to read aloud, and
 * the hyphens survive being pasted into a chat message. 54^12 is far past what
 * a login rate-limited to 8 tries per 15 minutes will ever be brute-forced at.
 */
function suggestPassword() {
  // No l/1/I/O/0: these are the characters people mistype from a screenshot.
  const alphabet = 'abcdefghijkmnpqrstuvwxyzACDEFGHJKLMNPQRSTUVWXYZ23456789';
  const n = 12;
  const bytes = new Uint32Array(n);
  window.crypto.getRandomValues(bytes);
  // A fresh random value per character. Modulo bias here is negligible (2^32
  // over 54 symbols) and the alternative — rejection sampling — buys nothing
  // for a password this long.
  const chars = Array.from(bytes, b => alphabet[b % alphabet.length]);
  return [chars.slice(0, 4), chars.slice(4, 8), chars.slice(8, 12)]
    .map(g => g.join('')).join('-');
}

export default function AccountMaker({ token, kind, what }) {
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [made, setMade] = useState(null);
  const [copied, setCopied] = useState('');

  const make = async () => {
    setBusy(true); setErr(''); setMade(null);
    try {
      const r = await fetch(`${API_BASE}/api/make-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), password: pw, kind }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Request failed (${r.status})`);
      setMade(j);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const copy = async (text, which) => {
    try { await navigator.clipboard.writeText(text); } catch { /* select-and-copy still works */ }
    setCopied(which);
    setTimeout(() => setCopied(''), 2000);
  };

  // Cleared as soon as it is handed over: this component should not sit there
  // holding a password after the one moment it needed it.
  const reset = () => { setName(''); setPw(''); setMade(null); setErr(''); };

  return (
    <div className="am">
      <div className="am-head">
        <h3>Create a login</h3>
        <p>
          Makes the line to paste into Vercel. The password is hashed here and never
          stored — {what} sign in with it, and the variable only ever holds the hash.
        </p>
      </div>

      <div className="am-form">
        <label className="am-f">
          <span>Account name</span>
          <input className="am-input" value={name} placeholder="engineering"
                 autoComplete="off" onChange={e => setName(e.target.value)} />
        </label>
        <label className="am-f">
          <span>Password</span>
          <div className="am-pwrow">
            <input className="am-input" value={pw} placeholder="at least 8 characters"
                   autoComplete="new-password" onChange={e => setPw(e.target.value)} />
            <button className="am-mini" type="button" onClick={() => setPw(suggestPassword())}>
              Suggest
            </button>
          </div>
        </label>
        <button className="am-btn" onClick={make}
                disabled={busy || !name.trim() || pw.length < 8}>
          {busy ? 'Hashing…' : 'Generate'}
        </button>
      </div>

      {err && <div className="am-err">{err}</div>}

      {made && (
        <div className="am-out">
          {made.duplicate ? (
            <div className="am-warn">
              <strong>{made.account}</strong> already exists in {made.variable}. Pasting this
              as a second entry would leave two accounts of the same name — <strong>replace</strong> the
              existing one instead, which is how you rotate a password.
            </div>
          ) : (
            <div className="am-note">
              Add this to <code>{made.variable}</code> in Vercel → Settings → Environment
              Variables, separated from the existing accounts by a comma. It takes effect on
              the next deploy.
            </div>
          )}

          <div className="am-copyrow">
            <code className="am-pair">{made.pair}</code>
            <button className="am-mini" onClick={() => copy(made.pair, 'pair')}>
              {copied === 'pair' ? 'Copied' : 'Copy'}
            </button>
          </div>

          {/* The password one more time, because it is about to be gone. The
              hash is one-way: nobody can recover this later, so if it is not
              written down or sent now, the account has to be made again. */}
          <div className="am-pwout">
            <span>Give <strong>{made.account}</strong> this password — it cannot be recovered later:</span>
            <div className="am-copyrow">
              <code className="am-pair">{pw}</code>
              <button className="am-mini" onClick={() => copy(pw, 'pw')}>
                {copied === 'pw' ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          {!!made.existing.length && (
            <div className="am-existing">
              Already in {made.variable}: {made.existing.join(', ')}
            </div>
          )}

          <button className="am-mini" onClick={reset}>Done</button>
        </div>
      )}
    </div>
  );
}

export const ACCOUNT_MAKER_CSS = `
.am { border:1px solid var(--line); border-radius:12px; padding:16px; margin-top:18px; }
.am-head h3 { margin:0 0 4px; font-size:15.5px; }
.am-head p { margin:0 0 12px; font-size:12.5px; color:var(--dim); max-width:62ch; }
.am-form { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
.am-f { display:flex; flex-direction:column; gap:4px; font-size:12px; color:var(--dim);
  flex:1; min-width:190px; }
.am-pwrow { display:flex; gap:6px; }
.am-pwrow .am-input { flex:1; min-width:0; }
/* --field and --on-accent are supplied by the host page. This component lives in
   a dark tool (Building Directories) and a light one (the Event Creator), so a
   literal here is wrong in one of them: the old #1a1526 label was unreadable on
   the Event Creator's indigo, and an input the same colour as its card is muddy
   on dark. Fallbacks keep it sane in a host that defines neither. */
.am-input { width:100%; box-sizing:border-box; background:var(--field, var(--panel, #fff));
  color:var(--text, inherit); border:1.5px solid var(--line); border-radius:8px;
  padding:8px 10px; font:inherit; font-size:14px; }
.am-input:focus { outline:none; border-color:var(--accent); }
.am-btn { background:var(--accent); border:1.5px solid var(--accent); color:var(--on-accent, #fff);
  border-radius:8px; padding:8px 16px; font:inherit; font-size:14px; font-weight:700;
  cursor:pointer; }
.am-btn:disabled { opacity:.5; cursor:default; }
.am-mini { background:none; color:inherit; border:1.5px solid var(--line); border-radius:8px;
  padding:7px 12px; font:inherit; font-size:13px; font-weight:600; cursor:pointer; white-space:nowrap; }
.am-mini:hover { border-color:var(--accent); }
.am-err { margin-top:10px; color:#ff8a80; font-size:13px; }

.am-out { margin-top:14px; display:flex; flex-direction:column; gap:10px; }
.am-note, .am-warn, .am-existing, .am-pwout { font-size:12.5px; color:var(--dim); }
.am-warn { color:#ffb74d; }
.am-note code { font-family:ui-monospace, SFMono-Regular, Menlo, monospace; }
.am-copyrow { display:flex; gap:8px; align-items:center; }
.am-pair { flex:1; min-width:0; overflow-x:auto; white-space:nowrap;
  font-family:ui-monospace, SFMono-Regular, Menlo, monospace; font-size:12px;
  background:rgba(127,127,127,.12); border-radius:8px; padding:9px 11px; }
.am-pwout { display:flex; flex-direction:column; gap:6px; }
`;
